import React, { useState } from "react";

function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(() => window.innerWidth <= 640);
  React.useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 640);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

function getPaymentDay(paymentDate) {
  if (!paymentDate) return null;
  if (typeof paymentDate === "number") return paymentDate;
  if (String(paymentDate).includes("-")) return new Date(paymentDate + "T00:00:00").getDate();
  return parseInt(paymentDate);
}

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val);

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

const COLORS = ["#4F7FFA", "#F97316", "#10B981", "#A855F7", "#EF4444", "#F59E0B"];

export default function Calendar({ accounts, allPayments, onBack }) {
  const isMobile = useIsMobile();
  const today = new Date();
  const [viewDate, setViewDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [selectedDay, setSelectedDay] = useState(null);

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrevMonth = new Date(year, month, 0).getDate();

  const prevMonth = () => setViewDate(new Date(year, month - 1, 1));
  const nextMonth = () => setViewDate(new Date(year, month + 1, 1));
  const goToday = () => { setViewDate(new Date(today.getFullYear(), today.getMonth(), 1)); setSelectedDay(null); };

  // Build a map of day -> accounts due, using actual next due date
  const dueDayMap = {};
  accounts.forEach((acct, i) => {
    if (!acct.paymentDate) return;

    let nextDue;
    // If full date string, use it directly
    if (typeof acct.paymentDate === "string" && acct.paymentDate.includes("-")) {
      nextDue = new Date(acct.paymentDate + "T00:00:00");
    } else if (acct.lastPaymentDate) {
      const last = new Date(acct.lastPaymentDate + "T00:00:00");
      nextDue = new Date(last.getFullYear(), last.getMonth() + 1, getPaymentDay(acct.paymentDate));
      if (nextDue < today) nextDue = new Date(nextDue.getFullYear(), nextDue.getMonth() + 1, getPaymentDay(acct.paymentDate));
    } else {
      nextDue = new Date(today.getFullYear(), today.getMonth(), getPaymentDay(acct.paymentDate));
      if (nextDue < today) nextDue = new Date(today.getFullYear(), today.getMonth() + 1, getPaymentDay(acct.paymentDate));
    }

    // Only show in the calendar month being viewed
    if (nextDue.getFullYear() === year && nextDue.getMonth() === month) {
      const day = nextDue.getDate();
      if (!dueDayMap[day]) dueDayMap[day] = [];
      dueDayMap[day].push({ ...acct, colorIdx: i });
    }
  });

  // Check if a payment was logged for an account in current month/year
  const isPaid = (acctId) => {
    const payments = allPayments[acctId] || [];
    return payments.some((p) => {
      const d = new Date(p.date + "T00:00:00");
      return d.getFullYear() === year && d.getMonth() === month;
    });
  };

  // Build calendar grid
  const cells = [];
  // Prev month days
  for (let i = firstDay - 1; i >= 0; i--) {
    cells.push({ day: daysInPrevMonth - i, type: "prev" });
  }
  // Current month days
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, type: "current" });
  }
  // Next month days
  const remaining = 42 - cells.length;
  for (let d = 1; d <= remaining; d++) {
    cells.push({ day: d, type: "next" });
  }

  const selectedAccounts = selectedDay ? (dueDayMap[selectedDay] || []) : [];
  const totalDue = selectedAccounts.reduce((s, a) => s + a.minPayment, 0);

  // Upcoming payments this month
  // eslint-disable-next-line no-unused-vars
  const upcomingThisMonth = Object.entries(dueDayMap)
    .map(([day, accts]) => ({ day: parseInt(day), accts }))
    .filter(({ day }) => {
      const date = new Date(year, month, day);
      return date >= today || (date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear());
    })
    .sort((a, b) => a.day - b.day);

  const accountsDueThisMonth = Object.values(dueDayMap).flat();
  const uniqueDueIds = [...new Set(accountsDueThisMonth.map(a => a.id))];
  const totalMonthlyDue = accounts.filter(a => uniqueDueIds.includes(a.id)).reduce((s, a) => s + a.minPayment, 0);
  const paidCount = uniqueDueIds.filter(id => isPaid(id)).length;
  const accountsWithDueDate = uniqueDueIds.length;

  return (
    <div style={styles.page}>

      <main style={{ ...styles.main, ...(isMobile ? { padding: "16px 12px" } : {}) }}>
        <button style={styles.backBtn} onClick={onBack}>← Back to Dashboard</button>
        <div style={{ ...styles.pageTitle, ...(isMobile ? { fontSize: 20 } : {}) }}>📅 Payment Calendar</div>
        <div style={styles.pageSubtitle}>Track all your payment due dates in one place</div>

        {/* Summary Row */}
        <div style={{ ...styles.summaryRow, ...(isMobile ? { gridTemplateColumns: "1fr 1fr", gap: 8 } : {}) }}>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Total Due This Month</div>
            <div style={{ ...styles.summaryValue, color: "#EF4444" }}>{formatCurrency(totalMonthlyDue)}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Payments Made</div>
            <div style={{ ...styles.summaryValue, color: "#10B981" }}>{paidCount} of {accountsWithDueDate}</div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Remaining</div>
            <div style={{ ...styles.summaryValue, color: "#F97316" }}>
              {formatCurrency(accounts.filter(a => a.paymentDate && !isPaid(a.id)).reduce((s, a) => s + a.minPayment, 0))}
            </div>
          </div>
          <div style={styles.summaryCard}>
            <div style={styles.summaryLabel}>Accounts with Due Date</div>
            <div style={{ ...styles.summaryValue, color: "#4F7FFA" }}>{accountsWithDueDate} of {accounts.length}</div>
          </div>
        </div>

        <div style={{ ...styles.layout, ...(isMobile ? { gridTemplateColumns: "1fr" } : {}) }}>
          {/* Calendar */}
          <div style={styles.calendarCard}>
            {/* Month Navigation */}
            <div style={styles.calendarNav}>
              <button style={styles.navBtn} onClick={prevMonth}>‹</button>
              <div style={styles.monthTitle}>
                {MONTHS[month]} {year}
                <button style={styles.todayBtn} onClick={goToday}>Today</button>
              </div>
              <button style={styles.navBtn} onClick={nextMonth}>›</button>
            </div>

            {/* Day Headers */}
            <div style={styles.dayHeaders}>
              {DAYS.map(d => <div key={d} style={styles.dayHeader}>{isMobile ? d.charAt(0) : d}</div>)}
            </div>

            {/* Calendar Grid */}
            <div style={styles.grid}>
              {cells.map((cell, i) => {
                const isToday = cell.type === "current" &&
                  cell.day === today.getDate() &&
                  month === today.getMonth() &&
                  year === today.getFullYear();
                const isDue = cell.type === "current" && dueDayMap[cell.day];
                const isSelected = cell.type === "current" && cell.day === selectedDay;
                const isPast = cell.type === "current" && new Date(year, month, cell.day) < new Date(today.getFullYear(), today.getMonth(), today.getDate());

                return (
                  <div
                    key={i}
                    style={{
                      ...styles.cell,
                      ...(cell.type !== "current" ? styles.cellOther : {}),
                      ...(isToday ? styles.cellToday : {}),
                      ...(isSelected ? styles.cellSelected : {}),
                      ...(isDue && cell.type === "current" ? { cursor: "pointer" } : {}),
                      ...(isMobile ? { minHeight: 44, padding: "4px 2px" } : {}),
                    }}
                    onClick={() => cell.type === "current" && setSelectedDay(cell.day === selectedDay ? null : cell.day)}
                  >
                    <div style={{
                      ...styles.cellDay,
                      ...(isToday ? styles.cellDayToday : {}),
                      ...(isPast && cell.type === "current" ? { color: "#bbb" } : {}),
                      ...(isMobile ? { fontSize: 11 } : {}),
                    }}>
                      {cell.day}
                    </div>

                    {/* Due date labels */}
                    {isDue && cell.type === "current" && (
                      <div style={{ display: "flex", flexDirection: "column", gap: 2, marginTop: 2 }}>
                        {dueDayMap[cell.day].slice(0, isMobile ? 1 : 2).map((acct) => {
                          const paid = isPaid(acct.id);
                          const color = COLORS[acct.colorIdx % COLORS.length];
                          return (
                            <div key={acct.id} style={{
                              fontSize: isMobile ? 8 : 10,
                              fontWeight: 600,
                              color: paid ? "#065F46" : "#fff",
                              background: paid ? "#D1FAE5" : color,
                              borderRadius: 3,
                              padding: isMobile ? "1px 3px" : "2px 5px",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "100%",
                              opacity: isPast ? 0.6 : 1,
                            }}>
                              {paid ? "✓ " : ""}{acct.name}
                            </div>
                          );
                        })}
                        {dueDayMap[cell.day].length > (isMobile ? 1 : 2) && (
                          <div style={{ fontSize: 9, color: "#888", fontWeight: 600 }}>
                            +{dueDayMap[cell.day].length - (isMobile ? 1 : 2)} more
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={styles.legend}>
              <div style={styles.legendItem}><div style={{ width: 28, height: 14, background: "#4F7FFA", borderRadius: 3, fontSize: 9, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600 }}>Name</div> &nbsp;Payment due</div>
              <div style={styles.legendItem}><div style={{ width: 28, height: 14, background: "#D1FAE5", borderRadius: 3, fontSize: 9, color: "#065F46", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 600 }}>✓</div> &nbsp;Payment made</div>
              <div style={{ ...styles.legendItem, gap: 6 }}><div style={{ ...styles.cellDayToday, width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>•</div> Today</div>
            </div>
          </div>

          {/* Side Panel */}
          <div style={styles.sidePanel}>
            {/* Selected Day Detail */}
            {selectedDay && selectedAccounts.length > 0 && (
              <div style={styles.sideCard}>
                <div style={styles.sideCardTitle}>
                  {MONTHS[month]} {selectedDay}, {year}
                </div>
                <div style={styles.sideCardSubtitle}>{selectedAccounts.length} payment{selectedAccounts.length !== 1 ? "s" : ""} due · {formatCurrency(totalDue)} total</div>
                {selectedAccounts.map((acct) => {
                  const color = COLORS[acct.colorIdx % COLORS.length];
                  const paid = isPaid(acct.id);
                  return (
                    <div key={acct.id} style={{ ...styles.dueItem, borderLeft: `3px solid ${color}` }}>
                      <div style={styles.dueItemTop}>
                        <div>
                          <div style={styles.dueItemName}>{acct.name}</div>
                          <div style={styles.dueItemType}>{acct.type}</div>
                        </div>
                        {paid
                          ? <div style={styles.paidBadge}>✓ Paid</div>
                          : <div style={{ ...styles.unpaidBadge, color, background: color + "22" }}>Due</div>
                        }
                      </div>
                      <div style={styles.dueItemAmount}>{formatCurrency(acct.minPayment)}/mo</div>
                    </div>
                  );
                })}
              </div>
            )}

            {selectedDay && selectedAccounts.length === 0 && (
              <div style={styles.sideCard}>
                <div style={styles.sideCardTitle}>{MONTHS[month]} {selectedDay}, {year}</div>
                <div style={{ fontSize: 13, color: "#888", marginTop: 8 }}>No payments due on this day.</div>
              </div>
            )}

            {/* Upcoming Payments */}
            <div style={styles.sideCard}>
              <div style={styles.sideCardTitle}>
                {month === today.getMonth() && year === today.getFullYear()
                  ? "Upcoming This Month"
                  : `${MONTHS[month]} ${year}`}
              </div>
              {accountsWithDueDate === 0 ? (
                <div style={{ fontSize: 13, color: "#888", marginTop: 8 }}>
                  No due dates set. Edit your accounts to add payment due dates.
                </div>
              ) : (
                Object.entries(dueDayMap)
                  .map(([day, accts]) => ({ day: parseInt(day), accts }))
                  .sort((a, b) => a.day - b.day)
                  .map(({ day, accts }) => {
                    const date = new Date(year, month, day);
                    const isPastDate = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
                    return (
                      <div
                        key={day}
                        style={{ ...styles.upcomingRow, opacity: isPastDate ? 0.5 : 1, cursor: "pointer" }}
                        onClick={() => setSelectedDay(day)}
                      >
                        <div style={styles.upcomingDate}>
                          <div style={styles.upcomingDay}>{day}</div>
                          <div style={styles.upcomingMonth}>{MONTHS[month].slice(0, 3)}</div>
                        </div>
                        <div style={styles.upcomingAccounts}>
                          {accts.map((acct) => {
                            const color = COLORS[acct.colorIdx % COLORS.length];
                            const paid = isPaid(acct.id);
                            return (
                              <div key={acct.id} style={styles.upcomingAccount}>
                                <div style={{ width: 8, height: 8, borderRadius: "50%", background: paid ? "#10B981" : color, flexShrink: 0 }} />
                                <span style={{ fontSize: 13, color: paid ? "#10B981" : "#1a1a2e", textDecoration: paid ? "line-through" : "none" }}>
                                  {acct.name}
                                </span>
                                <span style={{ fontSize: 12, color: "#888", marginLeft: "auto" }}>{formatCurrency(acct.minPayment)}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#F8F9FC", fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#1a1a2e" },
  header: { background: "#1a1a2e", padding: "0 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.15)" },
  headerInner: { maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", height: 64, gap: 10 },
  logoSub: { fontSize: 11, color: "#8899BB", letterSpacing: 1 },
  main: { maxWidth: 1100, margin: "0 auto", padding: "32px 24px" },
  backBtn: { background: "none", border: "1.5px solid #ddd", borderRadius: 8, padding: "8px 16px", fontSize: 14, cursor: "pointer", color: "#555", marginBottom: 24, fontWeight: 500 },
  pageTitle: { fontSize: 26, fontWeight: 800, marginBottom: 6 },
  pageSubtitle: { fontSize: 14, color: "#888", marginBottom: 24 },
  summaryRow: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginBottom: 24 },
  summaryCard: { background: "#fff", borderRadius: 12, padding: "16px 20px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)" },
  summaryLabel: { fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 },
  summaryValue: { fontSize: 22, fontWeight: 800 },
  layout: { display: "grid", gridTemplateColumns: "1fr 300px", gap: 20, alignItems: "start" },
  calendarCard: { background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", padding: "24px" },
  calendarNav: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 },
  navBtn: { background: "none", border: "1.5px solid #E5E7EB", borderRadius: 8, width: 36, height: 36, fontSize: 20, cursor: "pointer", color: "#555", display: "flex", alignItems: "center", justifyContent: "center" },
  monthTitle: { fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", gap: 12 },
  todayBtn: { fontSize: 12, fontWeight: 600, color: "#4F7FFA", background: "#EEF2FF", border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer" },
  dayHeaders: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", marginBottom: 4 },
  dayHeader: { textAlign: "center", fontSize: 12, fontWeight: 600, color: "#888", padding: "8px 0", textTransform: "uppercase" },
  grid: { display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 },
  cell: { minHeight: 90, padding: "6px 4px", borderRadius: 8, cursor: "default", transition: "background 0.15s" },
  cellOther: { opacity: 0.3 },
  cellToday: { background: "#EEF2FF" },
  cellSelected: { background: "#E0E7FF", outline: "2px solid #4F7FFA" },
  cellDay: { fontSize: 13, fontWeight: 600, marginBottom: 4, textAlign: "center" },
  cellDayToday: { background: "#4F7FFA", color: "#fff", borderRadius: "50%", width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 4px" },
  dotRow: { display: "flex", justifyContent: "center", gap: 3, flexWrap: "wrap" },
  dot: { width: 8, height: 8, borderRadius: "50%", flexShrink: 0 },
  dotMore: { fontSize: 9, color: "#888", fontWeight: 600 },
  legend: { display: "flex", gap: 16, marginTop: 16, paddingTop: 16, borderTop: "1px solid #F0F0F5", flexWrap: "wrap" },
  legendItem: { display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#888" },
  sidePanel: { display: "flex", flexDirection: "column", gap: 16 },
  sideCard: { background: "#fff", borderRadius: 14, boxShadow: "0 1px 6px rgba(0,0,0,0.08)", padding: "20px" },
  sideCardTitle: { fontSize: 15, fontWeight: 700, marginBottom: 4 },
  sideCardSubtitle: { fontSize: 12, color: "#888", marginBottom: 14 },
  dueItem: { background: "#F8F9FC", borderRadius: 8, padding: "12px 14px", marginBottom: 10 },
  dueItemTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 },
  dueItemName: { fontSize: 14, fontWeight: 600 },
  dueItemType: { fontSize: 11, color: "#888" },
  dueItemAmount: { fontSize: 16, fontWeight: 700 },
  paidBadge: { background: "#D1FAE5", color: "#065F46", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20 },
  unpaidBadge: { fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20 },
  upcomingRow: { display: "flex", gap: 12, padding: "10px 0", borderBottom: "1px solid #F0F0F5" },
  upcomingDate: { width: 36, flexShrink: 0, textAlign: "center" },
  upcomingDay: { fontSize: 18, fontWeight: 800, lineHeight: 1 },
  upcomingMonth: { fontSize: 10, color: "#888", textTransform: "uppercase" },
  upcomingAccounts: { flex: 1, display: "flex", flexDirection: "column", gap: 6 },
  upcomingAccount: { display: "flex", alignItems: "center", gap: 8 },
};
